import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DetailPageLayout from '../../components/crm/DetailPageLayout';
import NotesPanel from '../../components/crm/NotesPanel';
import UnifiedActivityTimeline from '../../components/crm/UnifiedActivityTimeline';
import EnrichmentHelper from '../../components/crm/EnrichmentHelper';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import companiesService, { Company } from '../../services/crm/companiesService';
import contactsService, { Contact } from '../../services/crm/contactsService';
import dealsService, { Deal } from '../../services/crm/dealsService';
import dealStagesService, { DealStage } from '../../services/crm/dealStagesService';
import { toast } from 'react-hot-toast';
import { Building2, Globe, Phone, Mail, MapPin, TrendingUp, Users, DollarSign, Target, ExternalLink, Briefcase } from 'lucide-react';

const CompanyDetail: React.FC = () => {
  const { companyId = '' } = useParams();
  const navigate = useNavigate();
  const { activeOrgId } = useCrmOrgStore();

  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // editable fields
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [industry, setIndustry] = useState('');
  const [size, setSize] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [postal, setPostal] = useState('');
  const [country, setCountry] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'contacts' | 'deals' | 'activity'>('overview');

  const enrichmentConfidenceDisplay = useMemo(() => {
    if (company?.enrichment_confidence == null) return '—';
    const value = company.enrichment_confidence > 1 ? company.enrichment_confidence : company.enrichment_confidence * 100;
    return `${Math.round(value)}%`;
  }, [company?.enrichment_confidence]);

  const enrichedAtDisplay = useMemo(() => {
    if (!company?.enriched_at) return '—';
    return new Date(company.enriched_at).toLocaleString();
  }, [company?.enriched_at]);

  const enrichmentSources = useMemo(() => company?.enrichment_sources || [], [company?.enrichment_sources]);

  const intentScoreDisplay = useMemo(() => {
    if (company?.account_intent_score == null) return '—';
    return Math.round(company.account_intent_score).toString();
  }, [company?.account_intent_score]);

  const missingEnrichmentFields = useMemo(() => {
    if (!company) return [] as string[];
    const missing: string[] = [];
    if (!company.domain) missing.push('Domain');
    if (!company.website) missing.push('Website');
    if (!company.industry) missing.push('Industry');
    if (!company.size) missing.push('Employee size');
    if (company.annual_revenue == null) missing.push('Annual revenue');
    if (company.arr_potential == null) missing.push('ARR potential');
    if (company.employee_count == null) missing.push('Employee count');
    return missing;
  }, [company]);

  useEffect(() => { document.title = 'CRM | Company'; }, []);

  const load = async () => {
    if (!activeOrgId || !companyId) return;
    setLoading(true); setError(null);
    try {
      const { data } = await companiesService.get(companyId, activeOrgId);
      if (!data) { setError('Company not found'); return; }
      setCompany(data as Company);
      setName(data.name || '');
      setDomain(data.domain || '');
      setWebsite((data as any).website || '');
      setPhone((data as any).phone || '');
      setIndustry((data as any).industry || '');
      setSize((data as any).size || '');
      setAddress1((data as any).address_line1 || '');
      setAddress2((data as any).address_line2 || '');
      setCity((data as any).city || '');
      setRegion((data as any).region || '');
      setPostal((data as any).postal_code || '');
      setCountry((data as any).country || '');
    } catch (e: any) {
      setError(e?.message || 'Failed to load company');
    } finally {
      setLoading(false);
    }
  };

// --- Subcomponent: CompanyDeals ---
const CompanyDeals: React.FC<{ companyId: string }> = ({ companyId }) => {
  const { activeOrgId } = useCrmOrgStore();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [stages, setStages] = useState<DealStage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // create form
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [stageId, setStageId] = useState('');
  const [closeDate, setCloseDate] = useState('');

  const load = async () => {
    if (!activeOrgId || !companyId) return;
    setLoading(true); setError(null);
    try {
      const [st, ds] = await Promise.all([
        dealStagesService.list(activeOrgId),
        dealsService.list({ organization_id: activeOrgId, company_id: companyId, limit: 50, offset: 0 })
      ]);
      setStages(st.data || []);
      setDeals(ds.data || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeOrgId, companyId]);

  const createDeal = async () => {
    if (!activeOrgId || !companyId || !title || !stageId) { setError('Organization, Company, Title and Stage are required'); return; }
    setLoading(true); setError(null);
    try {
      await dealsService.create({
        organization_id: activeOrgId,
        title,
        amount: amount ? Number(amount) : undefined,
        stage_id: stageId,
        company_id: companyId,
        close_date: closeDate || undefined
      });
      setTitle(''); setAmount(''); setStageId(''); setCloseDate('');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to create deal');
    } finally {
      setLoading(false);
    }
  };

  const stageCounts = stages.map(s => ({
    stage: s,
    count: deals.filter(d => d.stage_id === s.id).length
  }));

  return (
    <div className="pt-6 border-t space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-medium">Company Deals</h2>
        <Link className="text-blue-600" to={`/crm/deals?companyId=${encodeURIComponent(companyId)}`}>View on Board</Link>
      </div>
      {error && <div className="text-red-600 text-sm">{error}</div>}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {stageCounts.map(sc => (
          <div key={sc.stage.id} className="border rounded p-2 text-center">
            <div className="text-xs text-gray-500">{sc.stage.name}</div>
            <div className="text-xl font-semibold">{sc.count}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <input className="border rounded p-2" placeholder="Deal title" value={title} onChange={e => setTitle(e.target.value)} />
        <input className="border rounded p-2" placeholder="Amount (optional)" value={amount} onChange={e => setAmount(e.target.value)} />
        <select className="border rounded p-2" value={stageId} onChange={e => setStageId(e.target.value)}>
          <option value="">Select Stage</option>
          {stages.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
        </select>
        <input className="border rounded p-2" type="date" value={closeDate} onChange={e => setCloseDate(e.target.value)} />
        <button className="bg-green-600 text-white rounded px-4" onClick={createDeal} disabled={loading}>Add Deal</button>
      </div>

      {loading ? (
        <div>Loading…</div>
      ) : (
        <table className="min-w-full border">
          <thead>
            <tr className="bg-gray-50">
              <th className="p-2 text-left">Title</th>
              <th className="p-2 text-left">Stage</th>
              <th className="p-2 text-left">Amount</th>
              <th className="p-2 text-left">Updated</th>
            </tr>
          </thead>
          <tbody>
            {deals.map(d => (
              <tr key={d.id} className="border-t">
                <td className="p-2">{d.title}</td>
                <td className="p-2">{stages.find(s => s.id === d.stage_id)?.name || '—'}</td>
                <td className="p-2">{d.amount != null ? d.amount : '—'}</td>
                <td className="p-2">{new Date(d.updated_at).toLocaleString()}</td>
              </tr>
            ))}
            {deals.length === 0 && (
              <tr><td className="p-2 text-sm text-gray-500" colSpan={4}>No deals yet.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeOrgId, companyId]);

  const save = async () => {
    if (!activeOrgId || !companyId) return;
    setLoading(true); setError(null);
    try {
      const payload = {
        organization_id: activeOrgId,
        name,
        domain: domain || undefined,
        website: website || undefined,
        phone: phone || undefined,
        industry: industry || undefined,
        size: size || undefined,
        address_line1: address1 || undefined,
        address_line2: address2 || undefined,
        city: city || undefined,
        region: region || undefined,
        postal_code: postal || undefined,
        country: country || undefined
      };
      const { data } = await companiesService.update(companyId, payload);
      setCompany(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to save company');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DetailPageLayout
      title={company?.name || 'Company'}
      backLink="/crm#companies"
      backLabel="Back to Companies"
      actions={
        <button
          onClick={save}
          disabled={loading || !company}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save'}
        </button>
      }
    >
      <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-6 space-y-4">
        {!activeOrgId && (
          <div className="text-sm text-gray-600">Select or create an organization above to begin.</div>
        )}

        {error && <div className="text-red-600 text-sm">{error}</div>}
        {loading && <div>Loading…</div>}

        {company && (
          <>
            {/* Hero Header */}
            <div className="bg-white rounded-2xl shadow-sm ring-2 ring-blue-600 overflow-hidden mb-6">
              <div className="p-8">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-xl bg-blue-50 border-2 border-blue-600 flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold mb-2 text-gray-900">{company.name}</h1>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                        {company.industry && (
                          <span className="flex items-center gap-1"><Target className="w-4 h-4 text-blue-600" />{company.industry}</span>
                        )}
                        {company.size && (
                          <span className="flex items-center gap-1"><Users className="w-4 h-4 text-blue-600" />{company.size}</span>
                        )}
                        {company.website && (
                          <a href={company.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-700 transition">
                            <Globe className="w-4 h-4" />Visit Website<ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 border-t border-gray-200 px-8 py-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Contacts</div>
                    <div className="text-2xl font-bold text-gray-900">{company.buying_committee?.length || 0}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Account Tier</div>
                    <div className="text-2xl font-bold text-gray-900">{company.account_tier || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">ARR Potential</div>
                    <div className="text-2xl font-bold text-gray-900">{company.arr_potential ? `$${(company.arr_potential / 1000).toFixed(0)}k` : '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Health Score</div>
                    <div className="text-2xl font-bold text-gray-900">{company.account_intent_score || '—'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 mb-6">
              <div className="border-b border-gray-200 px-6">
                <nav className="flex gap-8">
                  {[
                    { id: 'overview', label: 'Overview', icon: Building2 },
                    { id: 'contacts', label: 'People', icon: Users },
                    { id: 'deals', label: 'Deals', icon: DollarSign },
                    { id: 'activity', label: 'Activity', icon: TrendingUp }
                  ].map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id as any)}
                      className={`flex items-center gap-2 px-1 py-4 text-sm font-medium border-b-2 transition ${
                        activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}>
                      <tab.icon className="w-4 h-4" />{tab.label}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {activeTab === 'overview' && (
            <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h2 className="font-medium">Profile</h2>
                <input className="border rounded p-2 w-full" placeholder="Name" value={name} onChange={e => setName(e.target.value)} />
                <input className="border rounded p-2 w-full" placeholder="Domain" value={domain} onChange={e => setDomain(e.target.value)} />
                <input className="border rounded p-2 w-full" placeholder="Website" value={website} onChange={e => setWebsite(e.target.value)} />
                <input className="border rounded p-2 w-full" placeholder="Phone" value={phone} onChange={e => setPhone(e.target.value)} />
                <input className="border rounded p-2 w-full" placeholder="Industry" value={industry} onChange={e => setIndustry(e.target.value)} />
                <input className="border rounded p-2 w-full" placeholder="Size (e.g., 11-50)" value={size} onChange={e => setSize(e.target.value)} />
              </div>
              <div className="space-y-2">
                <h2 className="font-medium">Address</h2>
                <input className="border rounded p-2 w-full" placeholder="Address line 1" value={address1} onChange={e => setAddress1(e.target.value)} />
                <input className="border rounded p-2 w-full" placeholder="Address line 2" value={address2} onChange={e => setAddress2(e.target.value)} />
                <input className="border rounded p-2 w-full" placeholder="City" value={city} onChange={e => setCity(e.target.value)} />
                <input className="border rounded p-2 w-full" placeholder="Region" value={region} onChange={e => setRegion(e.target.value)} />
                <input className="border rounded p-2 w-full" placeholder="Postal code" value={postal} onChange={e => setPostal(e.target.value)} />
                <input className="border rounded p-2 w-full" placeholder="Country" value={country} onChange={e => setCountry(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <EnrichmentHelper
                  entityType="company"
                  entityId={companyId}
                  organizationId={activeOrgId || ''}
                  currentData={{
                    domain: company.domain || undefined
                  }}
                  enrichmentStatus={{
                    confidence: company.enrichment_confidence || undefined,
                    lastEnriched: company.enriched_at || undefined,
                    sources: enrichmentSources
                  }}
                  onEnrichmentComplete={load}
                />
              </div>
              <div className="space-y-2">
                <h2 className="font-medium">Next best data</h2>
                <div className="rounded border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
                  {missingEnrichmentFields.length === 0 ? (
                    <div>All key enrichment fields are populated.</div>
                  ) : (
                    <ul className="list-disc space-y-1 pl-5">
                      {missingEnrichmentFields.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            <div>
              <button className="bg-blue-600 text-white rounded px-4" onClick={save} disabled={loading}>Save</button>
            </div>

            <div className="pt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-3">
              <Link className="text-blue-600" to={`/crm/companies/${encodeURIComponent(companyId)}/sites`}>Manage Sites</Link>
              <Link className="text-blue-600" to={`/crm/activities`}>Create Meeting</Link>
            </div>

            <div className="pt-6 border-t">
              <NotesPanel entityType="company" entityId={companyId} title="Company Notes" />
            </div>
            </div>
            )}

            {activeTab === 'contacts' && (
              <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-6">
                <CompanyContacts companyId={companyId} />
              </div>
            )}

            {activeTab === 'deals' && (
              <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-6">
                <CompanyDeals companyId={companyId} />
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-6">
                <UnifiedActivityTimeline
                  contactId={companyId}
                  organizationId={activeOrgId || ''}
                  entityType="company"
                  entityId={companyId}
                />
              </div>
            )}
          </>
        )}
      </div>
    </DetailPageLayout>
  );
};

export default CompanyDetail;

// --- Subcomponent: CompanyContacts ---

const CompanyContacts: React.FC<{ companyId: string }> = ({ companyId }) => {
  const { activeOrgId } = useCrmOrgStore();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'executive' | 'manager' | 'ic'>('all');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');

  const loadContacts = async () => {
    if (!activeOrgId || !companyId) return;
    setLoading(true); setError(null);
    try {
      const resp = await contactsService.list({ organization_id: activeOrgId, company_id: companyId, limit: 100, offset: 0 });
      setContacts(resp.data || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadContacts(); /* eslint-disable-next-line */ }, [activeOrgId, companyId]);

  const addContact = async () => {
    if (!activeOrgId) { setError('Organization is required'); toast.error('Organization is required'); return; }
    if (!firstName && !lastName) { setError('Provide at least a first or last name'); toast.error('Provide at least a first or last name'); return; }
    setLoading(true); setError(null);
    try {
      await contactsService.create({
        organization_id: activeOrgId,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        email: email || undefined,
        phone: phone || undefined,
        job_title: jobTitle || undefined,
        company_id: companyId
      });
      setFirstName(''); setLastName(''); setEmail(''); setPhone(''); setJobTitle('');
      await loadContacts();
      toast.success('Contact added');
    } catch (e: any) {
      setError(e?.message || 'Failed to add contact');
      toast.error('Failed to add contact');
    } finally {
      setLoading(false);
    }
  };

  const matchFilter = (c: Contact) => {
    const title = ((c as any).job_title || '').toLowerCase();
    if (filter === 'all') return true;
    if (filter === 'executive') {
      return /(chief|ceo|cfo|coo|cto|cmo|cio|vp|vice\s*president|director|head)/i.test(title);
    }
    if (filter === 'manager') {
      return /(manager|lead|supervisor)/i.test(title) && !/(director|vp|chief|c[io]o|cto|cmo)/i.test(title);
    }
    if (filter === 'ic') {
      return title && !/(manager|lead|supervisor|director|vp|chief|c[io]o|cto|cmo)/i.test(title);
    }
    return true;
  };

  const filtered = contacts.filter(matchFilter);

  return (
    <div className="pt-6 border-t space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-medium">People at this Company</h2>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Seniority:</label>
          <select className="border rounded p-2" value={filter} onChange={e => setFilter(e.target.value as any)}>
            <option value="all">All</option>
            <option value="executive">Executive (CEO/VP/Director)</option>
            <option value="manager">Manager/Lead</option>
            <option value="ic">Individual Contributor</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <input className="border rounded p-2" placeholder="First name" value={firstName} onChange={e => setFirstName(e.target.value)} />
        <input className="border rounded p-2" placeholder="Last name" value={lastName} onChange={e => setLastName(e.target.value)} />
        <input className="border rounded p-2" placeholder="Email (optional)" value={email} onChange={e => setEmail(e.target.value)} />
        <input className="border rounded p-2" placeholder="Phone (optional)" value={phone} onChange={e => setPhone(e.target.value)} />
        <input className="border rounded p-2" placeholder="Job title (optional)" value={jobTitle} onChange={e => setJobTitle(e.target.value)} />
        <button className="bg-green-600 text-white rounded px-4" onClick={addContact} disabled={loading}>Add Contact</button>
      </div>
      {error && <div className="text-red-600 text-sm">{error}</div>}
      <table className="min-w-full border">
        <thead>
          <tr className="bg-gray-50">
            <th className="p-2 text-left">Name</th>
            <th className="p-2 text-left">Email</th>
            <th className="p-2 text-left">Title</th>
            <th className="p-2 text-left">Created</th>
            <th className="p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(c => (
            <tr key={c.id} className="border-t">
              <td className="p-2">{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
              <td className="p-2">{c.email || '—'}</td>
              <td className="p-2">{(c as any).job_title || '—'}</td>
              <td className="p-2">{new Date(c.created_at).toLocaleString()}</td>
              <td className="p-2 text-right">
                <Link className="text-blue-600" to={`/crm/contacts/${encodeURIComponent(c.id)}`}>View</Link>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr>
              <td className="p-2 text-sm text-gray-500" colSpan={5}>No contacts match this filter.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
